<script setup lang="ts">
import { computed, h, type Component, type VNode } from "vue";
import { MenuEntry } from "../../models/menu-entry";

export type BcMenuEntryProps = {
  item: MenuEntry;
  subItemIcon?: VNode | Component;
  hasSeparator?: boolean;
  selected?: boolean;
  textWrap?: boolean;
  hasIcons?: boolean;
  hasSubText?: boolean;
};

export type BcMenuEntryEmits = {
  (event: "click", payload: MenuEntry): void;
  (event: "mouseenter"): void;
};

const {
  item,
  subItemIcon = h("svg", { class: "ionicon", viewBox: "0 0 512 512" }, [
    h("path", {
      fill: "none",
      stroke: "currentColor",
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      "stroke-width": "48",
      d: "M184 112l144 144-144 144",
    }),
  ]),
  hasSeparator = false,
  selected = false,
  textWrap = false,
  hasIcons = false,
  hasSubText = false,
} = defineProps<BcMenuEntryProps>();

const emit = defineEmits<BcMenuEntryEmits>();

const hasSubItemIcon = computed(() => {
  return item.subItems && item.subItems.length && subItemIcon;
});

async function onClick(event: MouseEvent): Promise<void> {
  if (item.disabled) return;

  event.stopImmediatePropagation();
  emit("click", item);
  if (item.callback) {
    await item.callback(event);
  }
}
</script>

<template>
  <component v-if="item.component" v-bind:is="item.component" />
  <div
    v-else
    class="menuItem"
    v-bind:class="{
      selected: selected,
      disabled: item.disabled,
      textWrap: textWrap,
    }"
    v-on:click.prevent="onClick($event)"
    v-on:contextmenu.prevent="onClick($event)"
    v-on:mouseenter="emit('mouseenter')"
  >
    <div class="menuItemWrapper">
      <slot name="before-item">
        <div v-if="item.icon" class="menuItemIcon">
          <component v-bind:is="item.icon" />
        </div>
      </slot>

      <div
        class="menuItemTextWrapper"
        v-bind:data-locid="item.text"
        v-bind:class="{
          noIcon: !item.icon,
          hasIcons: hasIcons,
          hasSubText: hasSubText,
        }"
      >
        <div class="menuItemText">
          {{ item.text }}
        </div>

        <div
          class="menuItemSubText"
          v-if="!hasSubItemIcon && item.subText && item.subText != ''"
        >
          {{ item.subText }}
        </div>
      </div>

      <component class="menuSubItemIcon" v-if="hasSubItemIcon" v-bind:is="subItemIcon" />
    </div>

    <slot name="subMenu" />
  </div>

  <div class="menuItemSeparator" v-if="hasSeparator" />
</template>

<style lang="less">
@import "../../styles/components/menu/bc-menu-entry.less";
</style>
